// splineUtils.js — Shared Catmull-Rom spline utilities for path/lane editing.

/** Samples per segment used for edit-mode hit detection and rendering. */
export const SPLINE_SAMPLES_EDIT = 12;

// Centripetal exponent. alpha=0.5 is the centripetal parameterization, which is
// proven to never cusp or self-intersect — unlike uniform (alpha=0) Catmull-Rom,
// which loops/overshoots when control points are unevenly spaced.
const CENTRIPETAL_ALPHA = 0.5;
// Floor for a knot interval so coincident/near-coincident points can't divide by
// zero. Keeps output finite and preserves the per-segment sample count.
const KNOT_EPSILON = 1e-9;

const lerp = (a, b, u) => ({
  lat: a.lat + (b.lat - a.lat) * u,
  lng: a.lng + (b.lng - a.lng) * u,
});

// Chord length with cosine-corrected longitude so the knot weighting isn't
// skewed by latitude (a degree of lng is shorter than a degree of lat away from
// the equator). Used only for relative knot spacing, not for display.
function knotChord(a, b) {
  const latRad = (((a.lat + b.lat) / 2) * Math.PI) / 180;
  const dLat = b.lat - a.lat;
  const dLng = (b.lng - a.lng) * Math.cos(latRad);
  return Math.hypot(dLat, dLng);
}

// Control point with reflected phantoms at the ends so the first/last segments
// have a well-defined, natural tangent (and a non-zero end knot interval).
function controlAt(points, i) {
  const n = points.length;
  if (i < 0) {
    return {
      lat: 2 * points[0].lat - points[1].lat,
      lng: 2 * points[0].lng - points[1].lng,
    };
  }
  if (i >= n) {
    return {
      lat: 2 * points[n - 1].lat - points[n - 2].lat,
      lng: 2 * points[n - 1].lng - points[n - 2].lng,
    };
  }
  return points[i];
}

/**
 * Centripetal Catmull-Rom spline through `points`; returns the interpolated
 * path (lat/lng). The curve passes through every control point.
 *
 * Drop-in contract (relied on by callers and the lane editor's segment math):
 *   - same signature and { lat, lng }[] in/out
 *   - emits EXACTLY `numSamplesPerSegment` points per segment, then appends the
 *     final endpoint → length === (n-1)*numSamplesPerSegment + 1 for n >= 2
 *   - out[i * numSamplesPerSegment] === points[i] (each segment starts on its node)
 *
 * @param points - Array of { lat, lng }
 * @param numSamplesPerSegment - samples per segment (default 16)
 * @returns Array of { lat, lng } along the curve
 */
export function catmullRomSpline(points, numSamplesPerSegment = 16) {
  if (!points?.length) return [];
  if (points.length === 1) return [points[0]];
  const out = [];
  const n = points.length;
  for (let i = 0; i < n - 1; i++) {
    const p0 = controlAt(points, i - 1);
    const p1 = controlAt(points, i);
    const p2 = controlAt(points, i + 1);
    const p3 = controlAt(points, i + 2);

    // Centripetal knot sequence.
    const t0 = 0;
    const t1 = t0 + Math.max(KNOT_EPSILON, Math.pow(knotChord(p0, p1), CENTRIPETAL_ALPHA));
    const t2 = t1 + Math.max(KNOT_EPSILON, Math.pow(knotChord(p1, p2), CENTRIPETAL_ALPHA));
    const t3 = t2 + Math.max(KNOT_EPSILON, Math.pow(knotChord(p2, p3), CENTRIPETAL_ALPHA));

    for (let j = 0; j < numSamplesPerSegment; j++) {
      // Sample uniformly within this segment's knot span [t1, t2).
      const t = t1 + (j / numSamplesPerSegment) * (t2 - t1);
      // Barry–Goldman pyramidal evaluation of the non-uniform Catmull-Rom.
      const a1 = lerp(p0, p1, (t - t0) / (t1 - t0));
      const a2 = lerp(p1, p2, (t - t1) / (t2 - t1));
      const a3 = lerp(p2, p3, (t - t2) / (t3 - t2));
      const b1 = lerp(a1, a2, (t - t0) / (t2 - t0));
      const b2 = lerp(a2, a3, (t - t1) / (t3 - t1));
      out.push(lerp(b1, b2, (t - t1) / (t2 - t1)));
    }
  }
  out.push(points[n - 1]);
  return out;
}
